import * as React from 'react';
import { ReactEditor, RenderElementProps, RenderLeafProps, useSlateStatic } from 'slate-react';
import { Editor, Path, Node } from 'slate';

const IS_ANDROID = typeof navigator !== 'undefined' && /Android/.test(navigator.userAgent);
const MARK_PLACEHOLDER_SYMBOL = Symbol('mark-placeholder') as unknown as string;

// https://github.com/ianstormtaylor/slate/blob/main/packages/slate-react/src/components/string.tsx#L13
const ZeroWidthString = (props: {
  length?: number;
  isLineBreak?: boolean;
  isMarkPlaceholder?: boolean;
}) => {
  const { length = 0, isLineBreak = false, isMarkPlaceholder = false } = props;

  const attributes: {
    'data-slate-zero-width': string;
    'data-slate-length': number;
    'data-slate-mark-placeholder'?: boolean;
  } = {
    'data-slate-zero-width': isLineBreak ? 'n' : 'z',
    'data-slate-length': length,
  };

  if (isMarkPlaceholder) {
    attributes['data-slate-mark-placeholder'] = true;
  }

  // FIXME: Inserting the \uFEFF on iOS breaks capitalization at the start of an
  // empty editor (https://github.com/ianstormtaylor/slate/issues/5199).
  //
  // However, not inserting the \uFEFF on iOS causes the editor to crash when
  // inserting any text using an IME at the start of a block. This appears to
  // be because accepting an IME suggestion when at the start of a block (no
  // preceding \uFEFF) removes one or more DOM elements that `toSlateRange`
  // depends on. (https://github.com/ianstormtaylor/slate/issues/5703)
  return (
    <span {...attributes}>
      {!IS_ANDROID || !isLineBreak ? '\uFEFF' : null}
      {isLineBreak ? <br /> : null}
    </span>
  );
};

// Paragraph element component
export const ParagraphElement: React.FC<RenderElementProps> = ({
  attributes,
  children,
  element,
}) => {
  if (['paragraph', 'p'].includes(element?.type)) {
    return (
      <p {...attributes} data-slate-node="element" data-slate-paragraph>
        {children}
      </p>
    );
  }
};

// https://github.com/ianstormtaylor/slate/blob/ba33735a8ca7ca7437e891365451d25a6646a4c7/packages/slate-react/src/components/leaf.tsx

export const ParagraphLeaf: React.FC<RenderLeafProps> = ({ attributes, children, leaf, text }) => {
  const editor: any = useSlateStatic();
  const path = ReactEditor.findPath(editor, text);
  const parentPath = Path.parent(path);
  const parent = Node.parent(editor, path);
  const isMarkPlaceholder = Boolean(leaf[MARK_PLACEHOLDER_SYMBOL]);

  // COMPAT: Render text inside void nodes with a zero-width space.
  // So the node can contain selection but the text is not visible.
  if (editor.isVoid(parent)) {
    return <ZeroWidthString length={Node.string(parent).length} />;
  }

  // COMPAT: If this is the last text node in an empty block, render a zero-
  // width space that will convert into a line break when copying and pasting
  // to support expected plain text.
  if (
    leaf.text === '' &&
    parent.children[parent.children.length - 1] === text &&
    !editor.isInline(parent) &&
    Editor.string(editor, parentPath) === ''
  ) {
    return <ZeroWidthString isLineBreak isMarkPlaceholder={isMarkPlaceholder} />;
  }

  // COMPAT: If the text is empty, it's because it's on the edge of an inline
  // node, so we render a zero-width space so that the selection can be
  // inserted next to it still.
  if (leaf.text === '') {
    return <ZeroWidthString isMarkPlaceholder={isMarkPlaceholder} />;
  }
  return children;
};
